import { useEffect, useCallback, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { IoLogoWhatsapp, IoLogoInstagram, IoLogoLinkedin, IoIosMail } from 'react-icons/io'
import { HiMiniDocumentArrowDown, HiMiniPencilSquare } from 'react-icons/hi2'

import { FlagArgentina, FlagBrasil, FlagChile, FlagColombia, FlagPeru, FlagUruguay, FlagMexico } from '../../Icons/Flags'
import Loading from '../../Components/Loading/Loading'

import { CircularBar } from '../../Components/FutureCV/ciricularBar'
import { NineBox } from '../../Components/FutureCV/nineBox'
import { Stars } from '../../Components/FutureCV/stars'
import { MetricsCard } from '../../Components/FutureCV/metricsCard'
import { ProgressBar } from '../../Components/FutureCV/progressBar'
import { BoxSalary } from '../../Components/FutureCV/boxSalary'
import { BoxCareer } from '../../Components/FutureCV/boxCareer'
import { Indicator } from '../../Components/FutureCV/Indicator'

import ls from '../../API/Lists/Lists'
import cv from '../../API/CVFuture'
import { getIndustryTypeList } from '../../API/Lists/industryType'

import AreasMapper from '../../Utils/AreasMapper'
import LevelsMapper from '../../Utils/LevelsMapper'
import SubareasMapper from '../../Utils/SubareasMapper'

import { calcAge, findMaxCulture, getLabel } from './methods'
import { futureCVTranslate } from '../../i18n/translatedMessages'

const flags = {
	1: <FlagChile width={22} height={22} />,
	2: <FlagArgentina width={22} height={22} />,
	3: <FlagPeru width={22} height={22} />,
	4: <FlagBrasil width={22} height={22} />,
	5: <FlagColombia width={22} height={22} />,
	6: <FlagMexico width={22} height={22} />,
	8: <FlagUruguay width={22} height={22} />,
}

export function FutureCV() {
	const { id } = useParams()
	const [data, setData] = useState(null)
	const [industries, setIndustries] = useState(null)
	const [enableEdit, setEnableEdit] = useState(false)

	const [stars, setStars] = useState({
		competence: 0,
		performance: 0,
	})

	const loadData = useCallback(async () => {
		try {
			const [areasRes, levelsRes, subareasRes] = await Promise.all([ls.areas(), ls.levels(), ls.subareas()])

			const areas = areasRes?.map(AreasMapper.toDomain)
			const levels = levelsRes?.map(LevelsMapper.toDomain)
			const subareas = subareasRes?.map(SubareasMapper.toDomain)

			const [employeeXP, knowledge, employee, [profile], culture, [profile360], [asset], [salary], career, [performance]] = await Promise.all([
				cv.getEmployeesExp(id),
				cv.getKnowledge(id),
				cv.getEmployeesById(id),
				cv.getEmployeeInformation(id),
				cv.getCulture(id),
				cv.get360Profile(id),
				cv.getAssetsEmployee(id),
				cv.getSalary(id),
				cv.getCareer(1340),
				cv.getEmployeePerformance(id),
			])

			//const salarySMTM = await cv.getSalarySMTM(employee?.employerUser?.email)

			setIndustries(await getIndustryTypeList())

			setData({
				experience: employeeXP[employeeXP.length - 1],
				knowledge: knowledge,
				profile: {
					...employee,
					...profile,
					asset: asset?.url ?? '',
					experience: calcAge(employeeXP[0].startYear),
				},
				culture: findMaxCulture(culture),
				profile360: profile360,
				salary: {
					...salary,
				},
				career: career
					.filter(item => item.chosen_by !== 'hr')
					.sort((a, b) => a.id - b.id)
					.slice(0, 3)
					.map(step => ({
						id: step.id,
						level: getLabel(levels, step.level_id),
						area: getLabel(areas, step.area_id),
						subarea: getLabel(subareas, step.subarea_id),
					})),
			})
		} catch (error) {
			console.log(error)
		}
	}, [])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleGeneratePDF = async () => {
		const element = document.getElementById('cv')

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
		})

		const imgData = canvas.toDataURL('image/png')
		const pdf = new jsPDF('p', 'mm', 'a4')

		const pageWidth = pdf.internal.pageSize.getWidth()

		const imgWidth = pageWidth
		const imgHeight = (canvas.height * imgWidth) / canvas.width

		console.log('PNG', 0, 0, imgWidth, imgHeight)
		pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)

		pdf.save('page-snapshot.pdf')
	}

	if (!data) return <Loading />

	return (
		<>
			<div id='cv'>
				{/* Header */}
				<div className='tw-relative tw-flex tw-items-center tw-justify-center tw-flex-col tw-h-[300px]'>
					<div className='tw-w-full tw-h-1/2 tw-bg-gradient-to-l tw-from-blue-500 tw-to-purple-500'></div>
					<div className='tw-w-full tw-h-1/2'></div>
					<div className='tw-absolute tw-px-8'>
						<div className='tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-6 tw-bg-white tw-rounded-lg border tw-w-[850px] xl:tw-w-fit xl:tw-min-w-[850px]'>
							<div className='tw-flex tw-items-center '>
								{/* NOME OU FOTO */}
								{data.profile?.asset ? (
									<img src={data.profile?.asset} alt={data.profile.name} className='tw-h-20 tw-w-20 tw-rounded-full tw-shadow-sm' />
								) : (
									<div className='tw-flex tw-items-center tw-justify-center tw-h-20 tw-w-20 tw-bg-gray-100 tw-text-purple-600 tw-font-bold tw-text-3xl tw-rounded-full tw-shadow-sm'>
										{data.profile.name.charAt(0)}
									</div>
								)}

								<div className='tw-ml-6'>
									<h1 className='tw-text-2xl tw-font-bold tw-text-gray-900'>{data.profile.name}</h1>
									<p className='tw-text-sm  tw-font-medium tw-flex tw-gap-1 tw-m-0 tw-mb-2'>
										<span className='tw-text-purple-600 tw-font-bold'>{data.experience.job}</span>
										en
										<span className='tw-text-purple-600 tw-font-bold'>{data.experience.company}</span>
										desde {data.experience.startYear}
									</p>
									<p className='tw-text-sm tw-text-gray-600 tw-font-bold tw-flex tw-items-center tw-gap-2'>
										{calcAge(data.profile.birth)} años
										<span className='tw-text-gray-300 tw-text-xl'>|</span>
										{flags[data.profile.country]}
										<span className='tw-inline-flex tw-items-center tw-font-bold'>{data.profile.city}</span>
									</p>
								</div>
							</div>

							{/* <div className='tw-flex tw-flex-col tw-items-end tw-space-y-4 tw-h-full tw-w-1/3'>
								<div className='tw-flex tw-gap-4'>
									<Link to='#'>
										<div className='tw-bg-gradient-to-br tw-from-[#5342D1] tw-via-[#DC2564] tw-to-[#FEC05F] tw-rounded-full tw-p-[2px] tw-w-[28px] tw-h-[28px] tw-flex tw-items-center tw-justify-center'>
											<IoLogoInstagram color='#FFF' className='tw-w-full tw-h-full' />
										</div>
									</Link>
									<Link to='#'>
										<IoLogoLinkedin color='#0A66C2' size={28} />
									</Link>
									<Link to='#'>
										<IoLogoWhatsapp color='#25D366' size={28} />
									</Link>
									<Link to='#'>
										<IoIosMail color='#9391A1' size={28} />
									</Link>
								</div>
								<div className='tw-flex tw-flex-wrap tw-gap-2 tw-justify-end '>
									{['Ambicioso', 'Focalizado', 'Persistente', 'Tenaz', 'Visionario', 'Imaginativo', 'Emocional', 'Racional'].map(
										(tag, index) => (
											<span
												key={index}
												className='tw-bg-gray-100 tw-text-gray-800 tw-text-xs tw-px-3 tw-py-1 tw-rounded-full tw-shadow-sm'
											>
												{tag}
											</span>
										)
									)}
								</div>
							</div> */}
						</div>
					</div>
				</div>

				{/* Experiência Profesional & Formação */}
				<div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-p-6 tw-border-t border-bottom container'>
					<div className='tw-pl-4 tw-pr-8 border-end'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Experiência Profissional</h2>
						<ul className='tw-space-y-4 tw-m-0 tw-p-0'>
							<li className='tw-flex tw-items-center'>
								<span className='tw-text-purple-600 tw-mr-3'>⏰</span>
								<span className='tw-text-gray-700'>{data?.profile?.experience} años de carrera</span>
							</li>
							<li className='tw-flex tw-items-center'>
								<span className='tw-text-purple-600 tw-mr-3'>💼</span>
								<span className='tw-text-gray-700'>{data?.experience?.job}</span>
							</li>
							<li className='tw-flex tw-items-center'>
								<span className='tw-text-purple-600 tw-mr-3'>📊</span>
								<span className='tw-text-gray-700'>
									{industries.find(item => Number(item.id) === Number(data?.experience?.industryTypeId))?.name}
								</span>
							</li>
						</ul>
					</div>

					<div className='tw-px-4'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Formação</h2>
						<ul className='tw-space-y-4 tw-m-0 tw-p-0'>
							{data.knowledge?.map(item => (
								<li key={item.id} className='tw-flex tw-items-center'>
									<span className='tw-text-teal-600 tw-mr-3'>🎓</span>
									<div>
										<p className='tw-text-gray-700 tw-m-0'>{item.course}</p>
										<p className='tw-text-gray-500 tw-text-sm tw-m-0'>{item.university}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>

				{/* Match Cutural & Perfil 9Box */}
				<div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-p-6 tw-border-t border-bottom container'>
					<div className='tw-pl-4 tw-pr-8 border-end'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Match Cultural</h2>
						<div className='tw-flex tw-items-center tw-gap-10'>
							<CircularBar value={data.culture.value} />
							<div className='tw-w-1/2'>
								<h5 className='tw-text-base tw-p-0 tw-m-0 tw-mb-2 tw-text-gray-400'>Predominante</h5>
								<h4 className='tw-text-base tw-font-bold tw-p-0 tw-m-0 tw-text-purple tw-uppercase'>{data.culture.label}</h4>
								<p className='tw-text-sm tw-flex tw-flex-wrap'>{futureCVTranslate[`${data.culture.label}TextFutureCV`]}</p>
							</div>
						</div>
					</div>

					<div className='tw-px-4'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Perfil 9Box</h2>
						<div className='tw-flex tw-items-center'>
							{data.profile360 ? (
								<div>
									<NineBox
										stars={{
											performance: data.profile360?.performance?.boxValue ?? 0,
											potential: data.profile360?.potential?.boxValue ?? 0,
										}}
									/>
									<div className='tw-space-y-5'>
										<Stars title='Desempenho' value={data.profile360?.performance?.boxValue ?? 0} />
										<Stars title='Potencial' value={data.profile360?.potential?.boxValue ?? 0} />
									</div>
								</div>
							) : (
								<div>Sin datos</div>
							)}
						</div>
					</div>
				</div>

				{/* Desempenho & Potencial */}

				<div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-p-6 tw-border-t border-bottom container'>
					{data.profile360 ? (
						<div>
							<div className='tw-pl-4 tw-pr-8 border-end'>
								<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Desempeño</h2>
								<MetricsCard metrics={data.profile360?.performance?.answers} value={data.profile360?.performance?.boxValue ?? '-'} />
							</div>

							<div className='tw-px-4'>
								<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Potencial</h2>
								<MetricsCard metrics={data.profile360?.potential?.answers} value={data.profile360?.potential?.boxValue ?? '-'} />
							</div>
						</div>
					) : (
						<div>Sin datos para Desempeño y Potencial</div>
					)}
				</div>

				{/* Competencias & Motivações */}
				{/* 
					<div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-p-6 tw-border-t border-bottom container'>
						<div className='tw-pl-4 tw-pr-8 border-end'>
							<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Competencias</h2>
							<ProgressBar label='Análise e Resolução de Problemas' value={8.7} />
						</div>

						<div className='tw-px-4'>
							<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Motivaciones</h2>
						</div>
					</div>
				*/}

				{/* Salario e Plan de Carrera */}
				<div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-p-6 tw-border-t border-bottom container'>
					<div className='tw-pl-4 tw-pr-8 border-end'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Salario</h2>
						<div className='tw-grid tw-grid-cols-2'>
							<ul className='tw-list-none tw-m-0 tw-p-0 tw-space-y-4'>
								<li className='tw-space-x-2'>
									<span className='tw-text-sm tw-font-bold'>Colaborador:</span>
									<span>
										{data.salary.typeCurrency} {data.salary.salary}
									</span>
								</li>
								<li className='tw-space-x-2'>
									<span className='tw-text-sm tw-font-bold'>Mercado:</span>
									<span> -- </span>
								</li>
								<li className='tw-space-x-2'>
									<span className='tw-text-sm tw-font-bold'>Comparativo:</span>
									<span> -- </span>
								</li>
							</ul>
							{/* <BoxSalary comparative={100} /> */}
						</div>
					</div>

					<div className='tw-px-4'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Plan de Carrera</h2>
						<ul className='tw-m-0 tw-p-0 tw-list-none tw-space-y-2'>
							{data.career.map(item => (
								<li key={item.id} className='border tw-p-2 tw-flex tw-gap-2 tw-rounded-md'>
									<Indicator up={item.level.id > data.profile?.levelId} />
									<BoxCareer item={item} />
								</li>
							))}
						</ul>
					</div>
				</div>

				{/* Observations */}
				{/* <div className='tw-p-6 tw-border-t border-bottom container'>
					<div className='tw-flex tw-gap-2 tw-justify-between'>
						<h2 className='tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4'>Observaciones</h2>
						<button
							onClick={() => {
								setEnableEdit(!enableEdit)
							}}
							className='tw-px-4 tw-text-sm tw-h-8 border tw-rounded-xl tw-flex tw-items-center tw-gap-3 tw-bg-white'
						>
							<HiMiniPencilSquare />
							Editar Observaciones
						</button>
					</div>
					{!enableEdit ? (
						<p>
							Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin id porta lacus. Suspendisse finibus nunc et sapien dapibus
							luctus. Duis orci erat, aliquet vitae ultricies sit amet, pretium id augue. Donec lacinia vestibulum sem, non hendrerit
							ipsum porttitor eu. Morbi dapibus est quis libero volutpat, at malesuada neque facilisis. Nunc bibendum nibh ut tellus
							laoreet, sed placerat augue congue. Nulla vel congue leo. Donec maximus ipsum dignissim tristique molestie. Nam vehicula
							eros vitae consectetur convallis. Ut odio arcu, scelerisque ac sollicitudin at, pharetra sed magna. Donec et nisl mauris.
						</p>
					) : (
						<div className='tw-bg-gray-200 border tw-rounded-md'>
							<textarea
								rows={10}
								value={'currentEmployeeData?.performance?.observation'}
								className={`tw-w-full tw-bg-transparent tw-p-2 border-0`}
							/>
							<div className='tw-w-full tw-flex tw-pb-2 tw-pr-2 tw-justify-end tw-gap-2'>
								<button className='tw-px-3 tw-py-1 tw-rounded-md border tw-bg-purple-500 hover:tw-bg-purple-700 tw-text-white'>
									Salvar
								</button>
								<button
									onClick={() => {
										setEnableEdit(false)
									}}
									className='tw-px-3 tw-py-1 tw-rounded-md border tw-bg-gray-50 hover:tw-bg-gray-100'
								>
									Cancelar
								</button>
							</div>
						</div>
					)}
				</div> */}
			</div>

			<div className='tw-absolute tw-bottom-10 tw-right-10 tw-p-1 tw-backdrop-blur-sm tw-bg-purple-300/50 tw-rounded-md tw-shadow-[0_0_4px_4px_rgba(170,173,247,0.5)]'>
				<button
					className='tw-flex tw-items-center tw-gap-2 hover:tw-bg-purple-700  tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 tw-rounded border-0'
					onClick={handleGeneratePDF}
				>
					<HiMiniDocumentArrowDown size={24} /> Generate PDF
				</button>
			</div>
		</>
	)
}
